import { Injectable } from "@angular/core";
import { ThemeService } from "../theme.service";
import { BrandInfoEntry, fromBrandInfoBase } from "@apps/patient-core/src/app/data_model/brand-info";
import { GRAPHQL_OPERATION_NAMES, HttpService } from "../http.service";
import { Constants } from "@apps/patient-core/constants";
import { CacheService } from "../cache.service";
import merge from "lodash/merge";
import { PatientEntry } from "@apps/patient-core/src/app/data_model/patient";
import { BrandInfoBase, BrandInfoSiteBase } from "@backend/graph/brand_info/brand-info-base";
import { UrlSiteAliasBase } from "@backend/graph/url_site_aliases/url-site-alias-base";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { BaseAuthService } from "./auth.service";

export type T_BrandThemeOverride = Pick<UrlSiteAliasBase, "colour" | "logo_url" | "owned_brand_id"> | null;

export type RestrictedSiteInfo = {
  site_id: string;
  site_name: string;
  colour: string;
  logo_url: string;

  //these never actually seem to be set - raised an issue for Jamie to look at as
  //he added this code
  owned_brand_id?: string;
};

@Injectable()
export abstract class BaseBrandService {
  private _brand: BrandInfoEntry;

  constructor(
    protected _themeService: ThemeService,
    protected _patientService: BasePatientsService,
    protected _httpService: HttpService,
    protected _authService: BaseAuthService,
    protected _cacheService: CacheService
  ) {
    this._patientService.onPatientInfo.subscribe(async (patientInfo: PatientEntry) => {
      const { portal_url_full } = patientInfo;

      if (portal_url_full?.includes("/practices/")) {
        const alias = portal_url_full.substring(portal_url_full.lastIndexOf("/") + 1);
        await this.setRestrictedSiteIdFromAlias(alias);
      }
    });
  }

  public set brand(brand: BrandInfoEntry) {
    // Need to merge rather than replace as we have two different brand_info objects
    if (this._brand) {
      merge(this._brand, brand);
    } else {
      this._brand = brand;
    }
    const { _themeOverride: themeOverride } = this;
    if (themeOverride?.colour) this._brand.colour = themeOverride.colour;
    if (themeOverride?.logo_url) this._brand.logo_url = themeOverride.logo_url;

    this._themeService.setup(this._brand.colour);
    window.document.title = this._brand.display_name;
  }

  public get brand(): BrandInfoEntry {
    return this._brand;
  }

  public get restrictedSiteId(): string {
    return this.restrictedSite?.site_id ?? "";
  }

  public set restrictedSiteId(siteId: string) {
    const site = this._brand.sites.find((s) => s.site_id === siteId);

    if (!site) return;

    this.restrictedSite = {
      site_id: siteId,
      site_name: site.site_name,
      colour: this._brand.colour,
      logo_url: this._brand.logo_url,
    };
  }

  public get restrictedSiteName(): string {
    return this.restrictedSite?.site_name ?? "";
  }

  private get _themeOverride(): T_BrandThemeOverride {
    const aliasInfo = this.restrictedSite;
    if (!aliasInfo) return null;
    return {
      colour: aliasInfo.colour,
      logo_url: aliasInfo.logo_url,
      owned_brand_id: aliasInfo.owned_brand_id,
    };
  }

  public get sitesWithPhoneNumber(): Array<BrandInfoSiteBase> {
    return this.brand.sites.filter((site) => site.site_phone_number);
  }

  public abstract get restrictedSite(): RestrictedSiteInfo | null;

  public abstract set restrictedSite(restrictedSiteInfo: RestrictedSiteInfo | null);

  public deleteRestrictedSite(): void {
    this._cacheService.deleteSession(Constants.RESTRICTED_SITE_STORAGE_KEY);
    this._cacheService.delete(Constants.RESTRICTED_SITE_STORAGE_KEY);
  }

  public async setRestrictedSiteIdFromAlias(alias: string): Promise<boolean> {
    const response = await this._httpService
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .query<any>(
        GRAPHQL_OPERATION_NAMES.URL_SITE_ALIAS,
        `
      {
        practice {
          url_site_alias(alias: "${alias}") {
            site_id
            site {
              name
            }
            colour
            logo_url
          }
        }
      }
    `
      )
      .pipe()
      .toPromise();

    const urlSiteAlias = response.data?.practice?.url_site_alias;
    const site_id = urlSiteAlias?.site_id || "";
    const site_name = urlSiteAlias?.site?.name || "";
    const colour = urlSiteAlias?.colour || "";
    const logo_url = urlSiteAlias?.logo_url || "";

    if (site_id && site_name) {
      this.restrictedSite = {
        site_id,
        site_name,
        colour,
        logo_url,
      };

      this._brand.colour = colour;
      this._brand.logo_url = logo_url;
      this._themeService.setup(this._brand.colour);

      return true;
    }

    return false;
  }

  public setupBrandingConfig(brand: BrandInfoBase): boolean {
    if (brand) {
      this.brand = fromBrandInfoBase(brand, this._patientService.patientInfo);
      return true;
    }
    return false;
  }
}
